import { useCallback, useEffect, useState } from 'react';

// AI 设置中心
//   - enabled: 总开关
//   - bronze/silver/gold: 三级套餐，每级里有各自的功能 toggle
//   - provider/apiKey: 接外部 LLM 时用，目前只读
//
// 持久化：localStorage('lolcoach.ai.v1')

const STORAGE_KEY = 'lolcoach.ai.v1';

export const DEFAULT_SETTINGS = {
  enabled: false, // 默认关 —— 强调"增值服务"

  bronze: {
    priorityHint: true,
    roleGapScan:  true,
    counterPick:  true,
    compScore:    true,
    opWarning:    true,
  },

  // 白银 / 黄金套餐当前全部锁定。开关存在，但 canUse 永远返回 false。
  // 未来用户购买 license 后，会有一个 `tier: 'silver' | 'gold'` 字段，
  // canUse 检查 tier 决定能否打开。
  silver: {
    winRatePredict:  false,
    teamComposition: false,
    powerSpike:      false,
    nextPickAI:      false,
    autoReplay:      false,
  },
  gold: {
    opponentProfile: false,
    playerPool:      false,
    whatIf:          false,
    similarBP:       false,
    aiChat:          false,
  },

  provider: 'local',   // 'local' | 'openai' | 'deepseek' | 'tongyi'
  apiKey: '',
};

// 用户当前的"套餐等级"。未来由 license / 后端校验返回。
// 现在硬编码 = 'bronze'：只有青铜功能能用，白银/黄金永远锁定。
const USER_TIER = 'bronze';
const TIER_ORDER = { bronze: 1, silver: 2, gold: 3 };

const deepMerge = (target, source) => {
  if (!source || typeof source !== 'object') return target;
  const out = { ...target };
  for (const k of Object.keys(source)) {
    if (source[k] && typeof source[k] === 'object' && !Array.isArray(source[k])) {
      out[k] = deepMerge(target[k] || {}, source[k]);
    } else {
      out[k] = source[k];
    }
  }
  return out;
};

const loadFromStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw);
    return deepMerge(DEFAULT_SETTINGS, parsed);
  } catch (e) {
    return DEFAULT_SETTINGS;
  }
};

const useAISettings = () => {
  const [settings, setSettings] = useState(loadFromStorage);

  // 每次变动写回 localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (e) {
      // ignore (隐私模式 / quota 满)
    }
  }, [settings]);

  const setEnabled = useCallback((on) => {
    setSettings((s) => ({ ...s, enabled: !!on }));
  }, []);

  const toggleEnabled = useCallback(() => {
    setSettings((s) => ({ ...s, enabled: !s.enabled }));
  }, []);

  // tier in 'bronze'|'silver'|'gold'，key 为该 tier 下的具体功能名
  const toggleFeature = useCallback((tier, key) => {
    setSettings((s) => {
      const next = { ...s, [tier]: { ...s[tier], [key]: !s[tier][key] } };
      return next;
    });
  }, []);

  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
  }, []);

  // 这个功能"用户当前能不能用"
  // 1) 总开关必须开
  // 2) tier 必须 <= 用户的套餐等级（青铜用户白银功能用不了）
  // 3) 该 tier 下的功能 toggle 必须开
  const canUse = useCallback((tier, key) => {
    if (!settings.enabled) return false;
    if ((TIER_ORDER[tier] || 99) > TIER_ORDER[USER_TIER]) return false;
    return !!(settings[tier] && settings[tier][key]);
  }, [settings]);

  // 用户能不能"看到"这个功能的 UI（看见 ≠ 能用）
  // 已锁的功能也要让用户看到（带锁标识，引导付费）
  const isVisible = useCallback((tier) => {
    // 全部可见 — 锁定态由 isLocked 判断
    return true;
  }, []);

  const isLocked = useCallback((tier) => {
    return (TIER_ORDER[tier] || 99) > TIER_ORDER[USER_TIER];
  }, []);

  return {
    settings,
    setEnabled,
    toggleEnabled,
    toggleFeature,
    resetSettings,
    canUse,
    isVisible,
    isLocked,
    userTier: USER_TIER,
    isAIOn: settings.enabled,
  };
};

export default useAISettings;
